import { useCallback, useEffect, useState } from 'react';
import { api, type DeviceRow } from '../api';

const LOG_LEVELS = ['ERROR', 'WARN', 'INFO', 'DEBUG', 'TRACE'];

// Mirrors the device's own allowlist in components/aws_iot/diag.c. The device
// rejects anything it does not recognise regardless; this just avoids
// offering buttons that would be refused.
const COMMANDS = ['self_test', 'dump_stats', 'wifi_status', 'memory', 'ping_aws', 'fw_version'];

const kb = (n?: number) => (n == null ? '—' : `${(n / 1024).toFixed(1)} KB`);

export default function Diagnostics() {
  const [device, setDevice] = useState<DeviceRow | null>(null);
  const [logs, setLogs] = useState<string>('');
  const [minutes, setMinutes] = useState(30);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setDevice(await api.device());
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      const { events } = await api.logs(minutes);
      setLogs(
        events
          .map((e) => {
            // The device wraps each line as JSON so the IoT rule can route it;
            // unwrap for display but fall back to raw if it is not ours.
            try {
              const p = JSON.parse(e.message);
              return `${new Date(e.timestamp).toLocaleTimeString()}  ${p.line ?? e.message}`;
            } catch {
              return `${new Date(e.timestamp).toLocaleTimeString()}  ${e.message}`;
            }
          })
          .join('\n') || 'No log lines in this window.'
      );
    } catch (err) {
      setLogs(`Could not load logs: ${err instanceof Error ? err.message : String(err)}`);
    }
  }, [minutes]);

  useEffect(() => {
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  async function run(fn: () => Promise<unknown>, label: string) {
    setBusy(true);
    setNote('');
    try {
      await fn();
      setNote(`${label} sent.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {error && <div className="banner"><strong>Error.</strong> {error}</div>}

      <div className="grid">
        <div className="card">
          <h2>Firmware</h2>
          <table className="kv">
            <tbody>
              <tr><th>Version</th><td>{device?.firmware ?? '—'}</td></tr>
              <tr><th>Git commit</th><td>{device?.gitSha ?? '—'}</td></tr>
              <tr><th>Partition</th><td>{device?.partition ?? '—'}</td></tr>
              <tr><th>ESP-IDF</th><td>{device?.idfVersion ?? '—'}</td></tr>
            </tbody>
          </table>
        </div>

        <div className="card">
          <h2>Runtime</h2>
          <table className="kv">
            <tbody>
              <tr><th>Free heap</th><td>{kb(device?.heapFree)}</td></tr>
              <tr><th>Free PSRAM</th><td>{kb(device?.psramFree)}</td></tr>
              <tr><th>Boot count</th><td>{device?.bootCount ?? '—'}</td></tr>
              <tr><th>Last reset</th><td>{device?.resetReason ?? '—'}</td></tr>
              <tr><th>Disconnect</th><td>{device?.disconnectReason ?? '—'}</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="card" style={{ marginTop: '1rem' }}>
        <h2>Log level</h2>
        <p className="sub" style={{ marginTop: 0 }}>
          Applied within one telemetry interval, without a reboot. Currently{' '}
          <strong>{device?.logLevel ?? '—'}</strong>. TRACE is compiled in but will fill
          CloudWatch quickly — put it back to INFO when you are done.
        </p>
        {LOG_LEVELS.map((lvl) => (
          <button key={lvl} className="action" disabled={busy}
                  onClick={() => run(() => api.setConfig({ logLevel: lvl }), `Log level ${lvl}`)}>
            {lvl}
          </button>
        ))}
      </div>

      <div className="card" style={{ marginTop: '1rem' }}>
        <h2>Commands</h2>
        {COMMANDS.map((c) => (
          <button key={c} className="action" disabled={busy}
                  onClick={() => run(() => api.command(c), c)}>
            {c}
          </button>
        ))}
        <button className="action danger" disabled={busy}
                onClick={() => {
                  if (confirm('Reboot the spa controller?')) {
                    run(() => api.command('reboot'), 'Reboot');
                  }
                }}>
          reboot
        </button>
        {note && <p className="msg">{note} Replies arrive on the device log below.</p>}
      </div>

      <div className="card" style={{ marginTop: '1rem' }}>
        <h2>Device log</h2>
        <div style={{ marginBottom: '.6rem' }}>
          {[15, 30, 120, 720].map((m) => (
            <button key={m} className="action" aria-pressed={minutes === m}
                    onClick={() => setMinutes(m)}>
              {m < 60 ? `${m}m` : `${m / 60}h`}
            </button>
          ))}
          <button className="action" onClick={loadLogs}>Refresh</button>
        </div>
        <pre className="logs">{logs}</pre>
      </div>
    </>
  );
}
